<template>
  <div class="p-4">
    <incremental-search
      :autoFocus="true"
      ref="incrementalSearch"
      :data="searchResult"
      icon="SearchIcon"
      placeholder="英名、日本名、またはMMSI番号で検索"
      @input="searchQueryUpdate"
      @selected="selected"
      :remainList="true"
    >
      <template v-slot:group="{ group_name }">
        <p class="aisea-text-14-700-white">
          {{ getGroupName(group_name) }}
        </p>
      </template>

      <template v-slot:vessel="{ suggestion }">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <div class="img-container w-8 mr-3 flex">
              <img
                :src="getIcon(suggestion.categoryId)"
                :alt="suggestion.name"
                class="responsive"
              />
            </div>
            <div class="leading-none mt-1">
              <!--              <p class="mb-1 font-semibold">{{ suggestion.name_ja || suggestion.name }}</p>-->
              <p class="mb-1 aisea-text-14-700-white">
                {{
                  getVesselName({
                    nameJa: suggestion.name_ja,
                    name: suggestion.name,
                  })
                }}
                <img
                  v-if="getCountryCode(suggestion.id)"
                  :src="getCountryCode(suggestion.id)"
                  border="1"
                  style="height: 1rem; margin-left: 1rem"
                />
              </p>
              <small class="aisea-text-12-400-white"
                >IMO: {{ suggestion.imo || "---" }} MMSI:
                {{ suggestion.mmsi || "---" }}<br />
                目的地: {{ getDt(suggestion.id) }} ETA:
                {{ getEta(suggestion.id) }}
              </small>
            </div>
          </div>
        </div>
      </template>

      <!-- No Items Slot -->
      <template v-slot:noResult>
        <div class="flex items-center">
          <feather-icon icon="InfoIcon" svgClasses="h-5 w-5" class="mr-4" />
          <span>見つかりませんでした</span>
        </div>
      </template>
    </incremental-search>
  </div>
</template>

<script>
import IncrementalSearch from "@/components/incremental-search/incrementalSearch.vue";
import { VesselMixin } from "@/mixins";
import moment from "moment-timezone";
import Mapbox from "mapbox-gl";
import { DateUtil } from "@/utils";
import Vue from "vue";
import { db } from "@/firebase/firebaseConfig.js";

import { createNamespacedHelpers, mapGetters } from "vuex";
const {
  mapActions: mapActionsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap,
} = createNamespacedHelpers("liveMap");

export default {
  name: "VesselSearch",
  components: {
    IncrementalSearch,
  },
  mixins: [VesselMixin],
  data() {
    return {
      expiredPopup: null,
      timerId: null,
      vesselsAppendixCacheMap: {},
    };
  },
  props: {
    map: Object,
  },
  computed: {
    ...mapStateOfLiveMap({
      searchResult: (state) => state.searchResult,
    }),
    ...mapGetters({
      vesselPositionCollection: "vesselPositionCollection",
    }),
    ...mapGettersOfLiveMap({
      getCountryAlpha2: "getCountryAlpha2",
    }),
  },
  watch: {},
  methods: {
    ...mapActionsOfLiveMap([
      "getLastPosition",
      "searchVessels",
      "showVesselDetail",
      "jumpTo",
    ]),
    getVesselName(payload) {
      if (payload.nameJa) {
        return `${payload.name}(${payload.nameJa})`;
      }
      return `${payload.name}`;
    },
    getGroupName(groupName) {
      if (groupName === "vessel") {
        return "船舶";
      } else if (groupName === "files") {
        return "プロット";
      } else if (groupName === "pages") {
        return "港";
      } else {
        return "バース";
      }
    },
    getDt(suggestionId) {
      return this.vesselsAppendixCacheMap[suggestionId] &&
        this.vesselsAppendixCacheMap[suggestionId].dt
        ? this.vesselsAppendixCacheMap[suggestionId].dt
        : "---";
    },
    getEta(suggestionId) {
      return this.vesselsAppendixCacheMap[suggestionId] &&
        this.vesselsAppendixCacheMap[suggestionId].eta &&
        this.vesselsAppendixCacheMap[suggestionId].eta.seconds
        ? moment(this.vesselsAppendixCacheMap[suggestionId].eta.seconds * 1000)
            .local()
            .format("YYYY年MM月DD日 HH:mm")
        : "---";
    },
    getCountryCode(suggestionId) {
      if (
        this.vesselsAppendixCacheMap[suggestionId] &&
        this.vesselsAppendixCacheMap[suggestionId].cc
      ) {
        let countryAlpha2 = this.getCountryAlpha2(
          this.vesselsAppendixCacheMap[suggestionId].cc,
        );
        return countryAlpha2
          ? require(`@/assets/images/flags/${countryAlpha2}.svg`)
          : null;
      } else {
        return null;
      }
    },
    async searchQueryUpdate(query) {
      await this.searchVessels(query.trim());
      this.$refs.incrementalSearch.updateSuggestionsList();

      this.searchResult.vessel.data.forEach(async (vessel) => {
        // Dt,ETAをfirestoreから取得
        if (!this.vesselsAppendixCacheMap[vessel.id]) {
          const vesselInfoDoc = await db.doc(`vessel_info/${vessel.id}`).get();
          Vue.set(
            this.vesselsAppendixCacheMap,
            vessel.id,
            vesselInfoDoc.data(),
          );
        }
      });
    },
    async selected(val) {
      await this.showVesselDetail(val.vessel.id);
      let lastPosition = await this.getLastPosition(val.vessel.id);
      if (
        lastPosition &&
        lastPosition.longitude !== null &&
        lastPosition.latitude !== null
      ) {
        let coordinates = {
          center: [lastPosition.longitude, lastPosition.latitude],
        };
        await this.jumpTo(coordinates);
      }
      await this.showNotifyTimeout(lastPosition);
    },
    getIcon(id) {
      let iconName = this.getVesselCategoryProps(id).icon;
      return require(`@/assets/images/icons/vessels/${iconName}`);
    },
    async showNotifyTimeout(lastPosition) {
      // lastPositionを参照する場合
      if (
        lastPosition &&
        lastPosition.longitude !== null &&
        lastPosition.latitude !== null
      ) {
        const tenMinutesAgo = moment().subtract(10, "minutes").utc().unix();
        const lastPosAt = moment(lastPosition.last_position_at).unix();

        // 最終計測時間が10分前の場合
        if (lastPosAt < tenMinutesAgo) {
          // [NOTE] lastPositionのみの場合はPolygonの描画ができないため吹き出しのみ出す
          if (this.expiredPopup !== null) {
            this.expiredPopup.remove();
            this.expiredPopup = null;
          }

          const name = lastPosition.vessel_name_ja || lastPosition.vessel_name;
          this.showPopup(
            name,
            lastPosition.longitude,
            lastPosition.latitude,
            lastPosition.last_position_at,
          );
        }
      } else {
        this.$vs.notify({
          text: "位置情報が取得できません。",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
          position: "top-center",
          time: 10000,
        });
      }
    },
    showPopup(name, longitude, latitude, timestamp) {
      const dateFormat = moment(timestamp).format("YYYY/MM/DD HH:mm");
      let elapsed = DateUtil.elapsedTime(dateFormat);

      if (this.timerId) {
        clearTimeout(this.timerId);
        this.timerId = null;
      }

      this.expiredPopup = new Mapbox.Popup({
        closeOnClick: false,
        closeButton: true,
        offset: 15,
      })
        .setLngLat([longitude, latitude])
        .setText(
          `${name}はこの位置で、${elapsed}(${dateFormat})に記録されました。`,
        )
        .addTo(this.map);
      this.expiredPopup.addClassName("expired-vessel-live-position-popup");
      this.timerId = setTimeout(() => {
        this.expiredPopup.remove();
      }, 60000); // expiredPopupは1分経過で自動で消える
    },
  },
  beforeDestroy() {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  },
};
</script>

<style lang="scss">
.expired-vessel-live-position-popup {
  max-width: 200px;
}

.expired-vessel-live-position-popup > .mapboxgl-popup-tip {
  border-top-color: #333333 !important;
  border-bottom-color: #333333 !important;
}

.expired-vessel-live-position-popup > .mapboxgl-popup-content {
  background-color: #333333 !important;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  color: #f2f2f2;
  padding: 12px;
}

.expired-vessel-live-position-popup
  > .mapboxgl-popup-content
  > .mapboxgl-popup-close-button {
  color: #f2f2f2 !important;
  font-size: 20px;
  padding-right: 4px;
}
</style>
